import { useState } from "react";
import { useAuth } from "./AuthContext";
import { getApiBase } from "./api";
import { Button } from "./Button";
import { Modal } from "./Modal";
import { Spinner } from "./Spinner";
import { useToast } from "./Toast";

interface LoginModalProps {
	isOpen: boolean;
	onClose: () => void;
}

export const LoginModal = ({ isOpen, onClose }: LoginModalProps) => {
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [isSubmitting, setIsSubmitting] = useState(false);
	const { login } = useAuth();
	const { showToast } = useToast();

	const handleGoogleLogin = () => {
		const returnTo = encodeURIComponent(window.location.href);
		window.location.href = `${getApiBase()}/api/v1/auth/google?return_to=${returnTo}`;
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setIsSubmitting(true);

		try {
			const success = await login(email, password);
			if (success) {
				showToast("Welcome back", "success");
				onClose();
				setEmail("");
				setPassword("");
			} else {
				showToast("Invalid credentials", "error");
			}
		} catch (_err) {
			showToast("Authentication failed", "error");
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<Modal isOpen={isOpen} onClose={onClose} title="Access Personal Workspace">
			<div
				style={{
					display: "flex",
					flexDirection: "column",
					gap: "1.5rem",
					padding: "1rem 0",
				}}
			>
				<p style={{ fontSize: "0.875rem", color: "var(--color-text-muted)" }}>
					This area is restricted to the platform owner. Please enter your
					credentials.
				</p>

				<Button
					onClick={handleGoogleLogin}
					style={{
						background: "white",
						color: "#3c4043",
						border: "1px solid #dadce0",
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						gap: "0.75rem",
						fontWeight: "500",
					}}
				>
					<svg width="18" height="18" viewBox="0 0 18 18">
						<title>Google Logo</title>
						<path
							fill="#4285F4"
							d="M17.64 9.2c0-.63-.06-1.25-.16-1.84H9v3.49h4.84c-.21 1.12-.84 2.07-1.79 2.7l2.85 2.22c1.67-1.53 2.63-3.79 2.63-6.57z"
						/>
						<path
							fill="#34A853"
							d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.85-2.22c-.79.53-1.8.84-3.11.84-2.39 0-4.41-1.61-5.13-3.78L1.04 13.5c1.47 2.93 4.5 4.92 8.01 4.92z"
						/>
						<path
							fill="#FBBC05"
							d="M3.87 10.66c-.19-.53-.3-1.1-.3-1.66s.11-1.13.3-1.66L1.04 5.17C.38 6.51 0 8.02 0 9s.38 2.49 1.04 3.83l2.83-2.17z"
						/>
						<path
							fill="#EA4335"
							d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58C13.46.89 11.43 0 9 0 5.49 0 2.46 1.99 1.04 4.92L3.87 7.1C4.59 4.93 6.61 3.58 9 3.58z"
						/>
					</svg>
					Sign in with Google
				</Button>

				<div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
					<hr
						style={{
							flex: 1,
							border: "0",
							borderTop: "1px solid var(--color-border)",
						}}
					/>
					<span
						style={{ fontSize: "0.75rem", color: "var(--color-text-muted)" }}
					>
						OR
					</span>
					<hr
						style={{
							flex: 1,
							border: "0",
							borderTop: "1px solid var(--color-border)",
						}}
					/>
				</div>

				<form
					onSubmit={handleSubmit}
					style={{
						display: "flex",
						flexDirection: "column",
						gap: "1.5rem",
					}}
				>
					<div
						style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}
					>
						<label
							htmlFor="email"
							style={{ fontSize: "0.75rem", fontWeight: "600" }}
						>
							Email Address
						</label>
						<input
							id="email"
							type="email"
							value={email}
							onChange={(e) => setEmail(e.target.value)}
							placeholder="samuel@adafia.io"
							style={{
								padding: "0.75rem",
								background: "var(--color-bg-secondary)",
								border: "1px solid var(--color-border)",
								borderRadius: "6px",
								color: "var(--color-text-main)",
								fontSize: "1rem",
								outline: "none",
							}}
						/>
					</div>

					<div
						style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}
					>
						<label
							htmlFor="password"
							style={{ fontSize: "0.75rem", fontWeight: "600" }}
						>
							Authorization Key
						</label>
						<input
							id="password"
							type="password"
							value={password}
							onChange={(e) => setPassword(e.target.value)}
							placeholder="••••••••"
							style={{
								padding: "0.75rem",
								background: "var(--color-bg-secondary)",
								border: "1px solid var(--color-border)",
								borderRadius: "6px",
								color: "var(--color-text-main)",
								fontSize: "1rem",
								outline: "none",
							}}
						/>
					</div>

					<Button type="submit" disabled={isSubmitting || !email || !password}>
						{isSubmitting ? <Spinner size="small" /> : "Authenticate"}
					</Button>
				</form>
			</div>
		</Modal>
	);
};
